import { isInIframe } from '@shared/utils/isInIframe';
import { useQueryParamHelper } from '@shared/core/queryString';

/**
 * True only inside Joy's own admin preview iframe — the website designer and the EventWorkspace
 * dashboard preview.
 *
 * Deliberately NOT bare `isInIframe()`. The guest site also runs in an iframe for Carbon embeds and
 * masked/custom domains, and those are real guest visits: guest UI must behave normally there, so
 * anything that hides or disables itself "in preview" has to use this narrower signal.
 *
 * Detected via either `?preview=true` or `ctx=adminGuestSitePreview`. Both are needed: the
 * EventWorkspace dashboard builds its iframe `src` with HTML-entity `&amp;` separators, which
 * mangles the `preview` key into `amp;preview` — the leading `ctx` param still parses, so it is the
 * reliable signal there.
 *
 * The two params are already self-scoping to Joy's own preview surfaces, so the `isInIframe()`
 * conjunct is a narrowing guard rather than the primary signal: it keeps a guest who happens to
 * carry `?preview=true` on a top-level URL from being treated as a preview. The trade-off is that
 * this returns `false` for a preview surface rendered outside an iframe — none exist today, so if
 * one is ever added, key off the params alone rather than widening the iframe check.
 */
export const useIsAdminGuestSitePreview = (): boolean => {
  const { getValueString } = useQueryParamHelper();
  return isInIframe() && (getValueString('preview') === 'true' || getValueString('ctx') === 'adminGuestSitePreview');
};
